/**
 * Shared locale/language helpers used by the nav language switcher, the
 * "Ansicht" settings page and the cross-device sync effect.
 *
 * Rendering is driven by the `locale` cookie (read SSR in `src/i18n/request.ts`).
 * Logged-in users additionally persist their choice on the backend
 * (`user.language`) so it follows them to other devices — see `LanguageSync`.
 */

export const LOCALE_COOKIE = "locale";

/** Supported interface languages, in display order. `code` matches both the
 * `locale` cookie values and the backend `Language` enum. */
export const LANGUAGES = [
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "ru", label: "Русский", flag: "🇷🇺" },
  { code: "tr", label: "Türkçe", flag: "🇹🇷" },
  { code: "pl", label: "Polski", flag: "🇵🇱" },
  { code: "ar", label: "العربية", flag: "🇸🇦" },
] as const;

export type LanguageCode = (typeof LANGUAGES)[number]["code"];

export const SUPPORTED_LOCALES = LANGUAGES.map((l) => l.code) as LanguageCode[];

export function isLanguageCode(value: unknown): value is LanguageCode {
  return (
    typeof value === "string" &&
    (SUPPORTED_LOCALES as string[]).includes(value)
  );
}

/** Read the current `locale` cookie on the client (undefined if unset). */
export function readLocaleCookie(): string | undefined {
  if (typeof document === "undefined") return undefined;
  return document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${LOCALE_COOKIE}=`))
    ?.split("=")[1];
}

/** Persist the chosen locale in the cookie for one year. Does not reload. */
export function setLocaleCookie(lang: LanguageCode) {
  document.cookie = `${LOCALE_COOKIE}=${lang}; path=/; max-age=31536000`; // 1 year
}
